import { createAction } from 'redux-actions';
import Constants from 'constants/actions';

import headerBackgroundDefaultCoords from 'config/sectionHeader/headerBackgroundDefaultCoords';
import { openModalDialog, closeModalDialog } from '@nextiva/crm-modals';
import { previewImage, getLibrarySelectedImage } from '@nextiva/engagement-ui';
import { resetEditorAction } from '@nextiva/rich-text-editor/lib/modules/editorStore/editorChangeActions';

import generateClientUuid from 'sharedParts/utils/generateClientUuid';

import { navigate } from './appActions';

import {
  getActiveEditorSection,
  getActiveEntityCollectionPath,
  getActiveSectionHeaderBackgroundCoords,
  getActiveSectionHeaderBackgroundImageSize,
  getActiveSectionHeaderBackgroundImageUrl,
  getCurrentRouterRootPath,
  getEditorEntityId,
  getEditorSectionHeader,
  getEditorSharedHeaderSection,
  hasPersistedSectionHeader,
  isActiveSectionHeaderExist,
} from '../reducers';

import api from '../api';

import { handleApiErrorsLegacy } from './actionsUtils';
import generateHeaderRichTextEditorId from '../utils/generateHeaderRichTextEditorId';
import getUrl from '../utils/getUrl';
import sectionEditorAnimateScrollTo from '../utils/scroll/sectionEditorAnimateScrollTo';
import isSectionLegit from '../utils/isSectionLegit';

import routerParams from '../config/routerParams';

import modalTypes from '../constants/AppModalTypes';
import { MEDIUM } from '../components/headerCustomizer/constants/headerLogoImageSizePresets';

export const clearSectionHeader = createAction(Constants.CLEAR_SECTION_HEADER);
export const clearSectionHeaders = createAction(
  Constants.CLEAR_SECTION_HEADERS,
);
export const finishHeaderPersistence = createAction(
  Constants.FINISH_HEADER_PERSISTENCE,
);
export const setSectionHeader = createAction(
  Constants.SET_SECTION_HEADER,
  (sectionName, header) => ({ sectionName, header }),
);
export const setSectionHeaders = createAction(Constants.SET_SECTION_HEADERS);
export const updateSectionHeader = createAction(
  Constants.UPDATE_SECTION_HEADER,
  (sectionName, header) => ({ sectionName, header }),
);
export const updateSectionHeaders = createAction(
  Constants.UPDATE_SECTION_HEADERS,
);

export const applySectionHeaders = (sectionName, header) => dispatch => {
  if (isSectionLegit(sectionName)) {
    dispatch(setSectionHeader(sectionName, header));
  }
};

export const persistRemoveSectionHeader = sectionName => (
  dispatch,
  getState,
) => {
  const state = getState();
  const data = getEditorSectionHeader(state, sectionName);
  dispatch(clearSectionHeader(sectionName));

  return api
    .removeHeaderImage(
      getActiveEntityCollectionPath(state),
      getEditorEntityId(state),
      { headerId: data.id },
    )
    .catch(rejection => {
      dispatch(applySectionHeaders(sectionName, data));

      return handleApiErrorsLegacy(rejection, dispatch);
    })
    .then(() => {
      dispatch(finishHeaderPersistence());
    });
};

export const persistAddHeaderToSection = (sectionName, data) => (
  dispatch,
  getState,
) => {
  const state = getState();
  dispatch(applySectionHeaders(sectionName, data));

  return api
    .addHeaderImage(
      getActiveEntityCollectionPath(state),
      getEditorEntityId(state),
      { headerImageData: data },
    )
    .catch(rejection => {
      dispatch(clearSectionHeaders(data.clientUuid));
      return handleApiErrorsLegacy(rejection, dispatch);
    })
    .then(data => {
      dispatch(applySectionHeaders(sectionName, data));
      dispatch(finishHeaderPersistence());
    });
};

export const persistChangeHeader = data => (dispatch, getState) => {
  const state = getState();
  const sectionName = getActiveEditorSection(state);
  const previousData = getEditorSectionHeader(state, sectionName);
  dispatch(applySectionHeaders(sectionName, data));

  return api
    .updateHeaderImage(
      getActiveEntityCollectionPath(state),
      getEditorEntityId(state),
      { headerId: previousData.id, headerImageData: data },
    )
    .catch(rejection => {
      dispatch(applySectionHeaders(sectionName, previousData));
      return handleApiErrorsLegacy(rejection, dispatch);
    })
    .then(() => dispatch(finishHeaderPersistence()));
};

export const persistUpdateHeader = data => (dispatch, getState) => {
  const state = getState();
  const sectionName = getActiveEditorSection(state);
  const nextData = {
    ...getEditorSectionHeader(state, sectionName),
    ...data,
  };

  return dispatch(persistChangeHeader(nextData));
};

export const resetHeader = () => (dispatch, getState) => {
  const state = getState();
  const sectionName = getActiveEditorSection(state);
  const headerBlock = getEditorSectionHeader(state, sectionName);
  dispatch(
    resetEditorAction(generateHeaderRichTextEditorId(headerBlock.clientUuid)),
  );

  return dispatch(
    persistUpdateHeader({
      backgroundImage: null,
      logoImage: null,
      backgroundColor: null,
      textContent: null,
    }),
  );
};

export const removeSectionHeader = sectionName => (dispatch, getState) => {
  const state = getState();

  if (!hasPersistedSectionHeader(state, sectionName)) {
    return;
  }
  return dispatch(persistRemoveSectionHeader(sectionName));
};

export const removeActiveSectionHeader = () => (dispatch, getState) => {
  const state = getState();
  const activeSection =
    getEditorSharedHeaderSection(state) || getActiveEditorSection(state);
  dispatch(removeSectionHeader(activeSection));
};

export const addHeaderToSection = sectionName => (dispatch, getState) => {
  const state = getState();

  if (isActiveSectionHeaderExist(state)) {
    return;
  }
  sectionEditorAnimateScrollTo(0);

  return dispatch(
    persistAddHeaderToSection(sectionName, {
      clientUuid: generateClientUuid(),
      sectionAssignee: sectionName,
    }),
  );
};

export const openHeaderCustomizer = sectionName => (dispatch, getState) => {
  const state = getState();
  const entityId = getEditorEntityId(state);
  sectionName =
    (isSectionLegit(sectionName) && sectionName) ||
    getEditorSharedHeaderSection(state) ||
    getActiveEditorSection(state);
  const sectionUrl = getUrl.section({
    root: getCurrentRouterRootPath(state),
    entityId,
    sectionName,
  });
  const url = `${sectionUrl}/${routerParams.headerCustomizer}`;
  return dispatch(navigate(url));
};

export const openInsertHeaderBackgroundImageModal = () =>
  openModalDialog({
    type: modalTypes.insertHeaderBackgroundImage,
  });

export const openInsertHeaderLogoImageModal = () =>
  openModalDialog({
    type: modalTypes.insertHeaderLogoImage,
  });

export const setHeaderBackgroundImage = ({ id: imageId, url: src }) => (
  dispatch,
  getState,
) => {
  const state = getState();
  const currentUrl = getActiveSectionHeaderBackgroundImageUrl(state);
  const backgroundCoords =
    currentUrl === src
      ? getActiveSectionHeaderBackgroundCoords(state)
      : headerBackgroundDefaultCoords;
  dispatch(
    persistUpdateHeader({
      backgroundImage: {
        imageId,
        src,
      },
      backgroundCoords,
    }),
  );
};

export const setHeaderLogoImage = image =>
  persistUpdateHeader({
    logoImage: {
      imageId: image.id,
      src: image.url,
      sizePreset: MEDIUM,
    },
  });

export const setHeaderLogoImageAlign = align =>
  persistUpdateHeader({
    logoImageAlign: align,
  });

export const setHeaderLogoWithTextAlign = align =>
  persistUpdateHeader({
    logoWithTextAlign: align,
  });

export const setHeaderBackgroundImageSize = size => (dispatch, getState) => {
  const state = getState();
  const currentSize = getActiveSectionHeaderBackgroundImageSize(state);
  const backgroundCoords =
    currentSize === size
      ? getActiveSectionHeaderBackgroundCoords(state)
      : headerBackgroundDefaultCoords;
  return dispatch(
    persistUpdateHeader({
      backgroundImageSize: size,
      backgroundCoords,
    }),
  );
};

export const setHeaderBackgroundColor = backgroundColor =>
  persistUpdateHeader({
    backgroundColor,
  });

export const setHeaderFullWidthFlag = isFullWidth =>
  persistUpdateHeader({
    isFullWidth,
    backgroundCoords: headerBackgroundDefaultCoords, // resetting coords to default @sinslav
  });

export const persistHeaderBackgroundPosition = backgroundCoords =>
  persistUpdateHeader({
    backgroundCoords,
  });

export const updateActiveSectionHeader = header => (dispatch, getState) => {
  const sectionName = getActiveEditorSection(getState());
  return dispatch(updateSectionHeader(sectionName, header));
};

export const setHeaderBackgroundPosition = backgroundCoords =>
  updateActiveSectionHeader({
    backgroundCoords,
  });

export const persistUpdateHeaderHeight = height =>
  persistUpdateHeader({
    height,
  });

export const updateHeaderHeight = height =>
  updateActiveSectionHeader({
    height,
  });

export const clearHeaderBackgroundImage = () =>
  persistUpdateHeader({
    backgroundImage: null,
    backgroundCoords: headerBackgroundDefaultCoords,
  });

export const clearHeaderLogoImage = () =>
  persistUpdateHeader({
    logoImage: null,
  });

export const insertHeaderBackgroundImageFromPreview = insertImageDialogId => image => dispatch => {
  dispatch(setHeaderBackgroundImage(image));
  dispatch(closeModalDialog(insertImageDialogId));
};

export const insertHeaderLogoImageFromPreview = insertImageDialogId => image => dispatch => {
  dispatch(setHeaderLogoImage(image));
  dispatch(closeModalDialog(insertImageDialogId));
};

export const insertHeaderLogoImageFromUploaderList = () => (
  dispatch,
  getState,
) => {
  const state = getState();
  const image = getLibrarySelectedImage(state);
  return dispatch(setHeaderLogoImage(image));
};

export const insertHeaderBackgroundImageFromUploaderList = () => (
  dispatch,
  getState,
) => {
  const image = getLibrarySelectedImage(getState());
  return dispatch(setHeaderBackgroundImage(image));
};

const openImagePreview = modalType => (
  insertImageDialogId,
  imageId,
) => dispatch => {
  dispatch(previewImage(imageId));
  dispatch(
    openModalDialog({
      type: modalType,
      imageId,
      insertImageDialogId,
    }),
  );
};

export const openHeaderBackgroundImagePreview = openImagePreview(
  modalTypes.headerBackgroundImagePreview,
);

export const openHeaderLogoImagePreview = openImagePreview(
  modalTypes.headerLogoImagePreview,
);

export const setHeaderContentEnabledState = isContentEnabled =>
  persistUpdateHeader({
    contentEnabled: isContentEnabled,
  });

export const openInsertHeaderLogoLinkModal = url =>
  openModalDialog({
    type: modalTypes.insertHeaderLogoImageLink,
    url,
  });

export const setHeaderLogoImageLink = url => (dispatch, getState) => {
  const state = getState();
  const sectionName = getActiveEditorSection(state);
  const { logoImage } = getEditorSectionHeader(state, sectionName);

  dispatch(
    persistUpdateHeader({
      logoImage: {
        ...logoImage,
        linkUrl: url,
      },
    }),
  );
};

export const setHeaderLogoImageSize = (size, sizePreset) => (
  dispatch,
  getState,
) => {
  const state = getState();
  const sectionName = getActiveEditorSection(state);
  const { logoImage } = getEditorSectionHeader(state, sectionName);

  dispatch(
    persistUpdateHeader({
      logoImage: {
        ...logoImage,
        size,
        sizePreset,
      },
    }),
  );
};

export const setHeaderLayout = layout =>
  persistUpdateHeader({
    layout,
  });

export const setHeaderTextContent = ({ content }) =>
  persistUpdateHeader({
    textContent: content,
  });
